import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLanguage, languages } from '@/i18n';
import { AnimatePresence, motion } from 'framer-motion';
import { SvgIcon } from './ui/SvgIcon';
import languageIconUrl from '@/assets/icons/language.svg?url';
import { CONFIG } from '@/config/Config';

export const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language.substring(0, 2).toUpperCase();

  // Calculate grid layout
  const maxRowsPerColumn = 6;
  const numColumns = Math.ceil(languages.length / maxRowsPerColumn);

  // Create a 2D array for the grid
  const languageGrid = Array.from({ length: numColumns }, (_, colIndex) => {
    const startIdx = colIndex * maxRowsPerColumn;
    return languages.slice(startIdx, startIdx + maxRowsPerColumn);
  });

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const changeLang = (lang: string) => {
    changeLanguage(lang);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (CONFIG.ONLY_EN) {
    return null;
  }

  return (
    <div
      ref={dropdownRef}
      className="absolute left-4 top-3 z-50 flex cursor-pointer items-center"
      onClick={toggleDropdown}
    >
      <div className="flex items-center space-x-2">
        <SvgIcon src={languageIconUrl} alt="Language" size={6} />
        <span className="font-oswald -mt-0.5 text-2xl text-white">
          {currentLang}
        </span>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute left-2 top-full z-50 mt-2 overflow-hidden rounded-md bg-primary shadow-lg"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="p-2">
              <div className="flex space-x-4">
                {languageGrid.map((column, colIndex) => (
                  <div key={colIndex} className="flex flex-col">
                    {column.map((lang) => (
                      <div
                        key={lang}
                        className={`font-oswald cursor-pointer rounded p-2 text-center text-xl uppercase transition-colors hover:bg-primary-foreground/10 ${
                          i18n.language.startsWith(lang)
                            ? 'bg-white bg-opacity-25 font-bold'
                            : ''
                        }`}
                        onClick={() => changeLang(lang)}
                      >
                        {lang.toUpperCase()}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
